"use client";

import React, { useEffect, useRef, useState } from "react";
import { ClipboardList, Stethoscope, Brain, FileText, Users, Icon } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
type ProcessStep = {
  id: number;
  title: string;
  description: string;
  icon: Icon;
};
const processSteps: ProcessStep[] = [{
  id: 1,
  title: "Assess Eligibility & Functional Impact",
  description: "Review NDIS access requirements through the lens of PDA. Document how demand avoidance impacts social interaction, communication, and self-management in daily life.",
  icon: ClipboardList
}, {
  id: 2,
  title: "Gather Clinical Evidence",
  description: "Collaborate with OTs, Psychologists, and Speech Pathologists to secure reports that specifically articulate the need for a low-demand, neuro-affirming environment.",
  icon: Stethoscope
}, {
  id: 3,
  title: "Draft Your Carer Statement",
  description: "Prepare a detailed account of the support intensity required. Focus on the 'lived experience' to help planners understand the hidden challenges of masking and anxiety.",
  icon: Brain
}, {
  id: 4,
  title: "The Planning Meeting & Review",
  description: "Engage with your LAC or Planner with clear goals. Ensure funding categories are flexible enough to accommodate child-led therapies and specialized support workers.",
  icon: FileText
}, {
  id: 5,
  title: "Download NDIS Prep Guide",
  description: "Access our comprehensive guide for Australian families, featuring functional impact templates and checklists to help you secure the right level of NDIS support.",
  icon: Users
}];
function TimelineStep({
  step,
  isLast,
  index
}: {
  step: ProcessStep;
  isLast: boolean;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const Icon = step.icon;
  return <div data-section-id="160592" ref={ref} data-index={index} className={`flex gap-6 md:gap-10 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      {}
      <div className="flex flex-col items-center">
        <div className="shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-full bg-secondary flex items-center justify-center text-secondary-foreground shadow-md z-10">
          <Icon className="w-6 h-6 md:w-8 md:h-8" strokeWidth={1.5} />
        </div>
        {!isLast && <div className="w-0.5 flex-1 bg-secondary/40 my-3 rounded-full" />}
      </div>

      {}
      <div className="pb-12 md:pb-16 pt-2 md:pt-3 flex-1">
        <h3 className="text-2xl md:text-3xl font-bold font-serif text-primary mb-3 tracking-tight">
          {step.title}
        </h3>
        <p className="text-foreground/80 font-sans text-lg md:text-xl leading-relaxed">
          {step.description}
        </p>
      </div>
    </div>;
}
export default function TheDiagnosticProcess() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="navigating-the-ndis" className="bg-background py-20 md:py-32 overflow-hidden">
      <div className="max-w-4xl mx-auto px-6 md:px-8">
        
        {}
        <div ref={headerRef} className={`text-center mb-16 md:mb-24 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-serif text-primary mb-6 tracking-tight">
            Navigating the NDIS
          </h2>
          <p className="text-xl md:text-2xl font-sans text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Securing support for a PDA profile in Australia requires a strategic approach. Follow our step-by-step pathway to prepare your application and plan effectively.
          </p>
        </div>

        {}
        <div className="flex flex-col">
          {processSteps.map((step, index) => <TimelineStep key={step.id} step={step} isLast={index === processSteps.length - 1} index={index} />)}
        </div>

      </div>
    </section>;
}